<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from 'vue';
import { Coordinates, CalculationMethod, PrayerTimes, Madhab } from 'adhan';
import moment from 'moment-timezone';
import 'moment/locale/id';
import 'moment-hijri';

// --- Local Storage Key ---
const LOCATION_STORAGE_KEY = 'adzanUserLocation';

// --- State Refs ---
const prayerTimesData = ref(null);
const error = ref(null);
const latitude = ref(null);
const longitude = ref(null);
const timezone = ref(null);
const locationAddress = ref(null);
const isLoading = ref(true);
const isLoadingAddress = ref(false);
const isUsingFallbackLocation = ref(false);
const currentTime = ref(new Date());
const currentGregorianDate = ref('');
const currentHijriDate = ref('');
let timerInterval = null;

// --- Constants ---
const PRAYER_NAMES = { fajr: 'Subuh', sunrise: 'Terbit', dhuhr: 'Dzuhur', asr: 'Ashar', maghrib: 'Maghrib', isha: 'Isya' };
const NEXT_PRAYER_CHECK_ORDER = ['fajr', 'dhuhr', 'asr', 'maghrib', 'isha'];
const PRAYER_DISPLAY_ORDER = ['fajr', 'sunrise', 'dhuhr', 'asr', 'maghrib', 'isha']; // Corrected typo

// --- Locale Setup ---
moment.updateLocale('id', {
  iMonths: ['Muharram', 'Safar', 'Rabiul Awal', 'Rabiul Akhir', 'Jumadil Awal', 'Jumadil Akhir', 'Rajab', 'Syaban', 'Ramadhan', 'Syawal', 'Zulkaidah', 'Zulhijah'],
  iMonthsShort: ['Muh', 'Saf', 'Rab Awal', 'Rab Akhir', 'Jum Awal', 'Jum Akhir', 'Raj', 'Sya', 'Ram', 'Syw', 'Zul', 'Zulhij']
});

// --- Storage Functions ---
const saveLocationToStorage = (locationData) => {
  try {
    if (locationData && typeof locationData.latitude === 'number' && typeof locationData.longitude === 'number') {
      localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify(locationData));
      console.log('Location saved to storage:', locationData);
    }
  } catch (e) {
    console.error('Failed to save location to localStorage:', e);
  }
};
const loadLocationFromStorage = () => {
  try {
    const storedData = localStorage.getItem(LOCATION_STORAGE_KEY);
    if (storedData) {
      const parsedData = JSON.parse(storedData);
      if (typeof parsedData.latitude === 'number' && typeof parsedData.longitude === 'number') {
        console.log('Location loaded from storage:', parsedData);
        return parsedData;
      }
    }
  } catch (e) {
    console.error('Failed to load or parse location from localStorage:', e);
  }
  return null;
};

// --- Date Formatting ---
const formatDate = () => {
  moment.locale('id');
  const now = moment(currentTime.value).tz(timezone.value || 'Asia/Jakarta');
  currentGregorianDate.value = now.format('dddd, D MMMM YYYY');
  currentHijriDate.value = now.format('iD iMMMM iYYYY') + ' H';
};

// --- Prayer Time Calculation ---
const getPrayerTimes = () => {
 isLoading.value = true;
 error.value = null;
 prayerTimesData.value = null;
 if (!latitude.value || !longitude.value || !timezone.value) { error.value = 'Cannot calculate times: Location/Timezone info missing.'; isLoading.value = false; return; }
 try {
    const coordinates = new Coordinates(latitude.value, longitude.value);
    const params = CalculationMethod.MuslimWorldLeague();
    params.fajrAngle = 20; params.ishaAngle = 18; params.madhab = Madhab.Shafi;
    const date = new Date(); const pt = new PrayerTimes(coordinates, date, params);
    const tz = timezone.value;
    prayerTimesData.value = {
      fajr: { date: pt.fajr, formatted: moment(pt.fajr).tz(tz).format('HH:mm') },
      sunrise: { date: pt.sunrise, formatted: moment(pt.sunrise).tz(tz).format('HH:mm') },
      dhuhr: { date: pt.dhuhr, formatted: moment(pt.dhuhr).tz(tz).format('HH:mm') },
      asr: { date: pt.asr, formatted: moment(pt.asr).tz(tz).format('HH:mm') },
      maghrib: { date: pt.maghrib, formatted: moment(pt.maghrib).tz(tz).format('HH:mm') },
      isha: { date: pt.isha, formatted: moment(pt.isha).tz(tz).format('HH:mm') },
    };
  } catch (e) { console.error("Error calculating prayer times:", e); error.value = `Failed to calculate prayer times: ${e.message || e}`; prayerTimesData.value = null;}
  finally { isLoading.value = false; }
};

// --- Address Fetching ---
const fetchAddress = async (lat, lon) => {
 if (!lat || !lon) return; isLoadingAddress.value = true; locationAddress.value = null;
 const preferredLanguage = 'en'; const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lon}&accept-language=${preferredLanguage}&zoom=18`;
 try {
    const response = await fetch(url, { method: 'GET', headers: { 'Accept': 'application/json' } });
    if (!response.ok) throw new Error(`Nominatim API error: ${response.status} ${response.statusText}`);
    const data = await response.json();
    if (data && data.display_name) { locationAddress.value = data.display_name; }
    else if (data && data.error) { throw new Error(`Nominatim error: ${data.error}`); }
    else { locationAddress.value = "Address not found."; }
  } catch (err) { console.error("Error fetching address:", err); locationAddress.value = "Could not fetch address."; }
  finally { isLoadingAddress.value = false; }
};

// --- Timezone Determination ---
const determineTimezone = (lat, lon) => {
 console.warn("Using simplified longitude check for timezone - Accuracy not guaranteed!"); if (lon < 105.0) return 'Asia/Jakarta'; if (lon >= 105.0 && lon < 120.0) return 'Asia/Makassar'; if (lon >= 120.0 && lon < 135.0) return 'Asia/Jayapura'; console.warn(`Could not determine specific Indonesian timezone for Lon ${lon}, falling back.`); return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

// --- set Fallback Location ---
const useFallbackLocation = async () => {
  console.warn("Using fallback location (Jakarta).");
  latitude.value = -6.2088;
  longitude.value = 106.8456;
  timezone.value = 'Asia/Jakarta'; // Explicitly WIB for Jakarta
  isUsingFallbackLocation.value = true; // Set the flag
  // Fetch address for Jakarta (optional, could hardcode)
  await fetchAddress(latitude.value, longitude.value);
  // Calculate times for Jakarta
  getPrayerTimes();
  formatDate();
  // Don't save fallback to localStorage
  isLoading.value = false; // Ensure loading finishes
};


// --- *** UPDATED: Core Logic to Fetch Fresh GPS Data *** ---
const fetchAndProcessGpsData = async () => {
  isLoading.value = true;
  error.value = null; // Clear previous errors
  // Don't clear address/times here, let fallback handle if needed

  if (!navigator.geolocation) {
    error.value = 'Geolocation is not supported. Using default location.';
    await useFallbackLocation(); // Use fallback
    return;
  }

  try {
    const position = await new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: false, timeout: 10000, maximumAge: 60000
      });
    });

    // --- GPS Success ---
    const lat = position.coords.latitude;
    const lon = position.coords.longitude;
    const tz = determineTimezone(lat, lon);

    latitude.value = lat;
    longitude.value = lon;
    timezone.value = tz;
    isUsingFallbackLocation.value = false; // <-- Set flag to false on success

    console.log(`Workspaceed fresh location: Lat ${lat}, Lon ${lon}, Timezone: ${tz}`);

    // Fetch address for the new location
    await fetchAddress(lat, lon);

    // Save the *actual* fetched data (including address)
    saveLocationToStorage({
        latitude: lat,
        longitude: lon,
        timezone: tz,
        address: locationAddress.value
    });

    // Calculate times for the new location
    getPrayerTimes(); // This sets isLoading false when done
    formatDate();

  } catch (err) {
    // --- GPS Error/Denied ---
    error.value = `Failed to get location: ${err.message}. Using default location.`;
    console.warn(error.value);
    await useFallbackLocation(); // Use fallback
    // isLoading is set to false inside useFallbackLocation
  }
};


// --- Compute Next Prayer Time ---
// ... (remains the same) ...
const nextPrayerInfo = computed(() => { const now = currentTime.value.getTime(); if (!prayerTimesData.value || !latitude.value) { return null; } for (const key of NEXT_PRAYER_CHECK_ORDER) { const prayer = prayerTimesData.value[key]; if (prayer && prayer.date instanceof Date && !isNaN(prayer.date) && prayer.date.getTime() > now) { return { key: key, name: PRAYER_NAMES[key], time: prayer.date, formattedTime: prayer.formatted }; } } try { const tomorrow = new Date(currentTime.value); tomorrow.setDate(tomorrow.getDate() + 1); const coordinates = new Coordinates(latitude.value, longitude.value); const params = CalculationMethod.MuslimWorldLeague(); params.fajrAngle = 20; params.ishaAngle = 18; params.madhab = Madhab.Shafi; const ptTomorrow = new PrayerTimes(coordinates, tomorrow, params); if (!(ptTomorrow.fajr instanceof Date) || isNaN(ptTomorrow.fajr)) { throw new Error("Invalid date calculated for tomorrow's Fajr"); } return { key: 'fajr', name: PRAYER_NAMES['fajr'], time: ptTomorrow.fajr, formattedTime: moment(ptTomorrow.fajr).tz(timezone.value).format('HH:mm') }; } catch(e) { console.error("Could not calculate tomorrow's Fajr:", e); return null; } });


// --- Lifecycle Hooks ---
onMounted(() => {
  moment.locale('id');
  isLoading.value = true;

  const storedLocation = loadLocationFromStorage();

  if (storedLocation) {
    // --- Load from Storage ---
    console.log("Using stored location data.");
    latitude.value = storedLocation.latitude;
    longitude.value = storedLocation.longitude;
    timezone.value = storedLocation.timezone;
    locationAddress.value = storedLocation.address || "Location loaded";
    isLoadingAddress.value = false;
    isUsingFallbackLocation.value = false; // <-- Stored data is not fallback

    getPrayerTimes(); // This sets isLoading false when done
    formatDate();

  } else {
    // --- First time visit: Fetch GPS (which might trigger fallback) ---
    console.log("No stored location found, fetching fresh GPS data.");
    fetchAndProcessGpsData(); // Handles loading state and fallback internally
  }

  // Start timer to update current time
  timerInterval = setInterval(() => {
    currentTime.value = new Date();
    formatDate();
  }, 60 * 1000);

});

onUnmounted(() => {
  if (timerInterval) { clearInterval(timerInterval); }
});

// Watch timezone changes
watch(timezone, (newTz) => {
    if (newTz) { formatDate(); }
});

</script>

<template>
  <div class="container py-3">
    <div class="position-absolute top-0 end-0 p-3">
      <button class="btn btn-light btn-sm" @click="fetchAndProcessGpsData" :disabled="isLoading" title="Update Location from GPS">
        <i class="bi icon-placeholder" :class="isLoading ? 'spinner-border spinner-border-sm' : 'bi-geo-fill'"></i>
        <span v-if="isLoading" class="visually-hidden">Loading...</span>
      </button>
    </div>

    <div class="text-center mb-3">
      <h5 class="mb-0">{{ currentGregorianDate || '...' }}</h5> <small class="text-muted">{{ currentHijriDate || '...' }}</small> </div>

    <div v-if="error" class="alert alert-danger" role="alert">
      <strong>Error:</strong> {{ error }}
    </div>

    <div class="card text-center border-0 shadow-sm mb-4" style="background: linear-gradient(to bottom, #56ccf2, #2f80ed); color: white;">
      <div class="card-body">
        <div v-if="!prayerTimesData && isLoading" class="py-4"> <div class="spinner-border spinner-border-sm" role="status">
                 <span class="visually-hidden">Loading...</span>
             </div>
             <p class="mt-1 mb-0">Loading next prayer...</p>
        </div>
        <div v-else-if="nextPrayerInfo" class="py-1"> <h4 class="card-title mb-1">{{ nextPrayerInfo.name }}</h4>
             <h1 class="display-4 fw-bold mb-2">{{ nextPrayerInfo.formattedTime }}</h1>
        </div>
        <div v-else class="py-4"> <p>Next prayer unavailable</p>
        </div>

        <p class="mb-0 opacity-75">
          <i class="bi bi-geo-alt-fill me-1 icon-placeholder"></i>
          <span v-if="isLoadingAddress">Loading address...</span>
          <span v-else-if="locationAddress">{{ locationAddress }}</span>
          <span v-else>Address unavailable</span>
        </p>
        <div v-if="isUsingFallbackLocation" class="mt-1" style="font-size: 0.75em; opacity: 0.8;">
             (Using default location - Allow GPS for accuracy)
        </div>
      </div>
    </div>

    <h4 class="text-center mb-3">Prayer Times</h4>
    <div class="list-group list-group-flush shadow-sm rounded mb-4"> <div v-if="!prayerTimesData && isLoading" class="list-group-item text-center text-muted">
          <div class="spinner-border spinner-border-sm text-muted" role="status">
              <span class="visually-hidden">Loading...</span>
          </div>
          <span class="ms-2">Loading times...</span>
      </div>
      <template v-else-if="prayerTimesData"> <template v-for="key in PRAYER_DISPLAY_ORDER" :key="key">
            <div v-if="prayerTimesData[key]"
                 class="list-group-item d-flex justify-content-between align-items-center"
                 :class="{
                   'active text-white fw-bold': nextPrayerInfo?.key === key,
                   'fst-italic text-muted': key === 'sunrise'
                 }"
                 :style="key === 'sunrise' ? 'font-size: 0.9em;' : ''"> <span>{{ PRAYER_NAMES[key] }}</span>
              <strong :class="{ 'text-white': nextPrayerInfo?.key === key }">
                {{ prayerTimesData[key].formatted }}
              </strong>
            </div>
          </template>
      </template>
       <div v-else class="list-group-item text-center text-muted">
           Prayer times unavailable.
       </div>
    </div>

    <div class="text-center text-muted mt-4" style="font-size: 0.8em;">
      <p>Calculation Method: Kemenag (Fajr 20°, Isha 18°, Asr Shafi'i)<br>
      Address data © OpenStreetMap contributors. <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">Usage Policy</a></p>
      <p>Version 2.21 (Improved Loading)</p> </div>

  </div>
</template>

<style scoped>
.icon-placeholder {
  display: inline-block;
  width: 16px;
  min-height: 16px;
  line-height: 1;
}
</style>